import { useCallback, useState } from 'react';


import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import {
  TagRead,
  useVehiclesRetrieveQuery,
  useVehicleTagsCreateMutation,
  VehicleRead,
  VehicleTagRead,
} from 'src/common/external/bambi-api/bambiApi';
import { TagModal } from 'src/common/tags/TagModal';
import formatServerError from 'src/common/util/serverErrorFormatter';

import { fleetSlice } from '../../fleet.slice';

import type {
  SelectOption,
  SelectOptionWithColor,
} from '@bambi/component-library/src/components/inputs/Select/SelectOption/SelectOption';

export default function VehicleTagModal() {
  const dispatch = useAppDispatch();
  const vehicleTagModal = useAppSelector(
    (state) => state.fleet.vehicleTagModal
  );
  const [error, setError] = useState<string | null>(null);
  const [saveVehicleTags] = useVehicleTagsCreateMutation();

  const {
    data: selectedVehicle,
    isLoading: isVehicleLoading,
    isFetching: isVehicleFetching,
    error: VehicleError,
  } = useVehiclesRetrieveQuery(
    {
      id: vehicleTagModal.vehicleId,
    },
    {
      skip: !vehicleTagModal.vehicleId,
    }
  );

  const closeModal = useCallback(() => {
    setError(null);
    dispatch(fleetSlice.actions.onCloseVehicleTags());
  }, [setError, dispatch]);

  const handleSubmit = useCallback(
    async (selectedTags: SelectOptionWithColor[], allTags: TagRead[]) => {
      try {
        const vehicleTags = selectOptionsToVehicleTags(
          selectedTags,
          allTags,
          selectedVehicle
        );

        await saveVehicleTags({
          vehicleTagBulkCreate: { tags: vehicleTags },
        }).unwrap();
        closeModal();
      } catch (error) {
        const message = formatServerError(error);
        setError(message);
      }
    },
    [closeModal, saveVehicleTags, selectedVehicle]
  );

  return (
    <TagModal
      header="Add Vehicle Tags"
      initialTagIds={selectedVehicle?.tags?.map((vehicleTag) => vehicleTag.tag)}
      modalProps={{
        open: vehicleTagModal.isOpen,
        setOpen: closeModal,
      }}
      isExternalLoading={isVehicleLoading || isVehicleFetching}
      externalError={
        VehicleError ? 'Failed to load existing Vehicle tags' : error
      }
      handleSaveTags={handleSubmit}
    />
  );
}

export const selectOptionsToVehicleTags = (
  selectedTags: SelectOption[],
  tags: TagRead[],
  selectedVehicle?: VehicleRead
): VehicleTagRead[] => {
  return selectedTags.map((selectedTag) => {
    const vehicleTag = tags.find((t) => t.id === selectedTag.value);
    if (!vehicleTag) {
      throw new Error(`Tag ${selectedTag.value} not found`);
    }

    if (!selectedVehicle) {
      throw new Error('Vehicle not found');
    }

    return {
      tag_name: vehicleTag.tag_name,
      color_hex: vehicleTag.color_hex,
      vehicle: selectedVehicle.id,
      tag: vehicleTag.id,
    } as VehicleTagRead;
  });
};
